import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import apiUrls from "../../../api";
import type { IAppLoaderAction } from "../../../common/state/loaderHandleMiddleware";
import type { RootState } from "../../../common/state/store";

export type LrsActorMode = "pseudonym" | "email";
export type LrsAnonymousMode = "ignore" | "per_session";
export type LrsScopeMode = "all" | "selected";
export type LrsStatus = "active" | "paused_auth" | "paused_failures" | "paused_plan" | "disabled";

export interface ILrsConfig {
  id: number;
  enabled: boolean;
  status: LrsStatus;
  endpoint: string | null;
  secret_last4: string | null;
  has_credentials: boolean;
  xapi_version: string;
  actor_mode: LrsActorMode;
  actor_mode_locked_at: string | null;
  anonymous_mode: LrsAnonymousMode;
  scope_mode: LrsScopeMode;
  projects: Array<{ id: number; name: string }>;
  include_navigation: boolean;
  emit_from: string | null;
  cursor_at: string | null;
  last_translated_at: string | null;
  first_emitted_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  last_error_code: string | null;
  consecutive_failures: number;
}

export interface ILrsOutboxBucket {
  count: number;
  oldest: string | null;
}

export interface ILrsStatusReport {
  config: ILrsConfig;
  outbox: Record<"pending" | "sending" | "sent" | "dead", ILrsOutboxBucket>;
  cursor: {
    at: string | null;
    lagSeconds: number | null;
    caughtUp: boolean;
    checkedAt: string | null;
  };
  emissionEnabled: boolean;
}

export interface ILrsConfigPatch {
  enabled?: boolean;
  endpoint?: string;
  auth_key?: string;
  auth_secret?: string;
  xapi_version?: string;
  actor_mode?: LrsActorMode;
  anonymous_mode?: LrsAnonymousMode;
  scope_mode?: LrsScopeMode;
  projects?: number[];
  include_navigation?: boolean;
  emit_from?: string | null;
  resume?: boolean;
}

export interface ILrsConnectionTest {
  ok: boolean;
  httpStatus: number | null;
  versions: string[] | null;
  error: string | null;
  errorCode: string | null;
  readDenied: boolean;
}

interface ILrsState {
  config: ILrsConfig | null;
  status: ILrsStatusReport | null;
  loaded: boolean;
}

const initialState: ILrsState = { config: null, status: null, loaded: false };

const rejectAxios = (err: unknown, rejectWithValue: (value: unknown) => unknown) => {
  if (axios.isAxiosError(err)) {
    return rejectWithValue(err?.response?.data);
  }
  throw err;
};

export const fetchLrsConfig = createAsyncThunk<ILrsConfig | null, IAppLoaderAction>(
  "lrs/fetchConfig",
  // eslint-disable-next-line no-empty-pattern
  async ({}, { rejectWithValue }) => {
    try {
      const res = await axios.get<ILrsConfig | null>(apiUrls.lrs.config);
      return res.data;
    } catch (err) {
      return rejectAxios(err, rejectWithValue) as never;
    }
  },
);

export const saveLrsConfig = createAsyncThunk<
  ILrsConfig,
  { patch: ILrsConfigPatch } & IAppLoaderAction
>("lrs/saveConfig", async ({ patch }, { rejectWithValue }) => {
  try {
    const res = await axios.put<ILrsConfig>(apiUrls.lrs.config, patch);
    return res.data;
  } catch (err) {
    return rejectAxios(err, rejectWithValue) as never;
  }
});

export const fetchLrsStatus = createAsyncThunk<ILrsStatusReport | null, IAppLoaderAction>(
  "lrs/fetchStatus",
  // eslint-disable-next-line no-empty-pattern
  async ({}, { rejectWithValue }) => {
    try {
      const res = await axios.get<ILrsStatusReport | null>(apiUrls.lrs.status);
      return res.data;
    } catch (err) {
      return rejectAxios(err, rejectWithValue) as never;
    }
  },
);

export const testLrsConnection = createAsyncThunk<
  ILrsConnectionTest,
  {
    endpoint?: string;
    auth_key?: string;
    auth_secret?: string;
    xapi_version?: string;
  } & IAppLoaderAction
>("lrs/testConnection", async ({ componentId, ...body }, { rejectWithValue }) => {
  try {
    const res = await axios.post<ILrsConnectionTest>(apiUrls.lrs.testConnection, body);
    return res.data;
  } catch (err) {
    return rejectAxios(err, rejectWithValue) as never;
  }
});

export const replayLrsHistory = createAsyncThunk<
  ILrsConfig,
  { emit_from: string } & IAppLoaderAction
>("lrs/replay", async ({ emit_from }, { rejectWithValue }) => {
  try {
    const res = await axios.post<ILrsConfig>(apiUrls.lrs.replay, { emit_from });
    return res.data;
  } catch (err) {
    return rejectAxios(err, rejectWithValue) as never;
  }
});

export const lrsSlice = createSlice({
  name: "lrs",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchLrsConfig.fulfilled, (state, action) => {
      state.config = action.payload;
      state.loaded = true;
    });
    builder.addCase(saveLrsConfig.fulfilled, (state, action) => {
      state.config = action.payload;
    });
    builder.addCase(replayLrsHistory.fulfilled, (state, action) => {
      state.config = action.payload;
    });
    builder.addCase(fetchLrsStatus.fulfilled, (state, action) => {
      state.status = action.payload;
      if (action.payload) {
        state.config = action.payload.config;
      }
    });
  },
});

export const getLrsConfig = (state: RootState) => state.lrs.config;
export const getLrsStatus = (state: RootState) => state.lrs.status;
export const getLrsLoaded = (state: RootState) => state.lrs.loaded;

export const lrsReducer = lrsSlice.reducer;
